'use client';
import { AuthorInfo } from '@c/AuthorInfo';
import BlogCommentSection from '@c/BlogCommentSection';
import { ShareAltIcon } from '@c/icons';
import BaseModal from '@c/modals/BaseModal';
import ShareModal from '@c/modals/ShareModal';
import { useMutation } from '@tanstack/react-query';
import Button from '@ui/Button';
import BlogLink from '@ui/Link/BlogLink';
import SafeImage from '@ui/SafeImage';
import { deleteBlog } from '@util/firestore/blog';
import { isMobile } from '@util/index';
import { BlogDocument } from '@util/types/firestore/blog';
import { useAuth } from 'context/AuthContext';
import { injectArticleScript, injectVideoScript } from 'features/schemaMarkups';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import Script from 'next/script';
import { useState } from 'react';

interface BlogDetailPageProps {
  blog: BlogDocument;
  otherPosts: BlogDocument[];
}

const BlogDetailPage = ({ blog, otherPosts }: BlogDetailPageProps) => {
  const [openModal, setOpenModal] = useState(false);
  const { userDoc } = useAuth();
  const canEdit = (() => {
    if (userDoc?.roles?.admin || userDoc?.roles?.seo) return true;
    if (!userDoc?.uid) return false;
    if (!userDoc?.roles?.blog) return false;
    return userDoc?.uid === blog.uid;
  })();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const deleteBlogMutation = useMutation({
    mutationFn: () => deleteBlog(blog.id),
    onSuccess: () => {
      router.push('/blog');
    },
  });

  const router = useRouter();

  function handleDeleteClick() {
    setIsDeleteModalOpen(true);
  }

  function handleModalDeleteClick() {
    deleteBlogMutation.mutate();
  }

  return (
    <>
      <Script
        id="schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: injectArticleScript(blog) ?? '',
        }}
      />
      {blog.video && (
        <Script
          id="videoSchema"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: injectVideoScript(blog) ?? '',
          }}
        />
      )}

      <div className="mx-auto mb-[2rem] mt-[6rem] w-full max-w-[79.2rem] px-4 sm:px-0">
        {/* ADMIN/AUTHOR TOOLS */}
        {canEdit && (
          <div>
            <div className="flex gap-4">
              <Link
                type="button"
                className="rounded-md bg-brand-green px-4 py-1 font-medium text-brand-white"
                href={`/blog/edit/${blog.id}`}
              >
                Edit
              </Link>
              <button
                type="button"
                className="rounded-md bg-brand-red px-4 py-1 font-medium text-brand-white"
                onClick={handleDeleteClick}
              >
                Delete
              </button>
            </div>
            <BaseModal
              isOpen={isDeleteModalOpen}
              dismiss={() => setIsDeleteModalOpen(false)}
              title="Are you sure you want to delete this blog?"
            >
              <div className="flex h-full grow gap-4 p-8">
                <Button
                  disabled={deleteBlogMutation.isPending}
                  onClick={handleModalDeleteClick}
                  type="error"
                  text="YES, delete this blog."
                />
                <Button
                  onClick={() => setIsDeleteModalOpen(false)}
                  text="NO, keep this blog."
                  type="success"
                />
              </div>
            </BaseModal>
          </div>
        )}
        <h1 className="text-[3.2rem] font-semibold sm:text-left sm:text-[4.4rem] lg:text-center">
          {blog.title}
        </h1>
        {/* tags */}
        <div className="mt-4 flex justify-center gap-[.8rem] sm:mt-[2.4rem] sm:justify-start">
          {blog.tags.map((tag) => (
            <div
              key={tag}
              className="select-none rounded-[4rem] bg-brand-primary-lighter px-4 py-[.7rem] text-[1.3rem] "
            >
              {tag}
            </div>
          ))}
        </div>
        {/* author info */}
        <div className="mt-[4rem]  flex  items-center justify-between gap-[2rem] ">
          <AuthorInfo blog={blog} />

          <div className="font-semibold text-brand-primary">
            <Button
              type="text"
              text={isMobile() ? '' : 'Share This Blog'}
              leadingIcon={<ShareAltIcon />}
              noXPadding
              onClick={() => setOpenModal(true)}
            />
          </div>
          <ShareModal
            isOpen={openModal}
            title={blog.title}
            thumb={blog.thumbnail}
            id={blog.id}
            type="blog"
            dismiss={() => setOpenModal(false)}
          />
        </div>
        {!blog.hide_thumbnail && (
          <div className="relative mt-[4rem] h-[22rem] w-full max-w-[79.2rem] sm:h-[56rem]">
            <SafeImage
              className="overflow-clip rounded-[1.6rem] object-contain"
              alt={blog.title}
              src={isMobile() ? blog.thumbnail : blog.full_image}
              key={isMobile() ? blog.thumbnail : blog.full_image}
              width={1300}
              height={900}
            />
          </div>
        )}

        {/* body */}
        <div
          id="blog-content" // Used to make links correct color in global.css
          className="editor-content mt-[4rem] sm:mt-[6rem]"
          dangerouslySetInnerHTML={{
            __html: blog.html
              .replaceAll('<h1', '<h2')
              .replaceAll('/h1>', '/h2>'),
          }}
        />
        {/* CTA */}

        {/* comment section */}
        <BlogCommentSection blogId={blog.id} />

        {/* pagination */}
      </div>
      {/* other posts */}
      <div className="flex flex-col gap-[0.8rem] p-[0.8rem]">
        <h3 className="text-[2.4rem] font-semibold">Related Blogs</h3>
        <div className="mb-[1.2rem] flex flex-col flex-wrap gap-[2.4rem] sm:flex-row sm:justify-center">
          {otherPosts.map((post) => {
            return <BlogLink key={post.id} blog={post} />;
          })}
        </div>
      </div>
    </>
  );
};

export default BlogDetailPage;
